<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, hydrateOnVisible, onMounted, reactive, ref } from 'vue'

import { useClient } from '../../utils/client-only'
import { sendGTMEvent } from '../../utils/gtm'
import FacebookLogin from './FacebookLogin.vue'
import PasswordField from './PasswordField.vue'
import { useSessionStore } from '../../stores/session'
import { useNotifyStore } from '../../stores/notify'
import { useConfigStore } from '../../stores/config'

import type { AxiosError } from 'axios'

type FacebookAuthResponse = {
  accessToken: string
  userID: string
} | null

const LoginMethodPreference = defineAsyncComponent({
  loader: () => import('./LoginMethodPreference.vue'),
  hydrate: hydrateOnVisible()
})

const props = defineProps<{
  onSuccess?: () => void
}>()

const emit = defineEmits<{
  input: [event: Event]
}>()

const { isClient } = useClient()

const configStore = useConfigStore()
const { rsConfig } = storeToRefs(configStore)

const sessionStore = useSessionStore()
const { isLoggedIn, user: userFromStore } = storeToRefs(sessionStore)
const { login, facebookLogin } = sessionStore

const { showNotification } = useNotifyStore()

const emailInput = ref<HTMLInputElement | null>(null)

const preventSubmit = ref(true)
const showGlobalError = ref(false)
const processing = ref<boolean | null>(null)

const errorMsg = reactive<{ email: string | null, password: string | null }>({
  email: null,
  password: null
})

const user = reactive({
  email: '',
  password: ''
})

// socialLoginEnabled is still typed as env-keyed even though the server pre-resolves it, like the rest of RsConfig
const facebookEnabled = computed(() => (rsConfig.value.socialSettings?.socialLoginEnabled as unknown as { facebook?: boolean } | undefined)?.facebook)

const clearForm = () => {
  user.email = ''
  user.password = ''
}

const clearValidationError = (property: 'email' | 'password') => {
  errorMsg[property] = null
  if (showGlobalError.value) {
    showGlobalError.value = false
  }
}

const validateFormData = (): boolean => {
  errorMsg.email = user.email.length === 0 ? 'Bitte trage deine E-Mail-Adresse ein.' : null
  errorMsg.password = user.password.length === 0 ? 'Bitte trage dein Passwort ein.' : null

  return Object.values(errorMsg).some(message => message)
}

const notifyLoginSuccess = () => {
  if (!props.onSuccess) { return }
  const gtmEvent = { event: 'login', userId: userFromStore.value?.userId }
  sendGTMEvent(gtmEvent, { userId: undefined })
  props.onSuccess()
}

const submit = async () => {
  processing.value = true
  try {
    showGlobalError.value = false
    if (validateFormData()) {
      processing.value = null
      showNotification({
        type: 'error',
        title: 'Fehler beim Anmelden',
        message: 'Bitte trage deine Zugangsdaten ein.'
      })
      return
    }
    await login({ username: user.email, password: user.password })
    notifyLoginSuccess()
  } catch (error) {
    console.error(error)
    showGlobalError.value = true
    showNotification({
      type: 'error',
      title: 'Fehler beim Anmelden',
      message: 'Bitte überprüfe deine Eingaben.'
    })
  }
  processing.value = null
}

const onFacebookLogin = async (authResponse: FacebookAuthResponse) => {
  if (!authResponse) { return }
  showGlobalError.value = false
  processing.value = true
  const { accessToken, userID: facebookId } = authResponse
  try {
    if (!accessToken || !facebookId) {
      throw new Error('Facebook access token or id missing')
    }
    try {
      await facebookLogin({ accessToken, facebookId })
      notifyLoginSuccess()
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 404) {
        showNotification({
          type: 'error',
          title: 'Facebook-Anmeldung fehlgeschlagen',
          message: 'Du hast noch kein Facebook-Konto registriert. Weiterleitung zur Registrierungsseite.'
        })

        setTimeout(() => { window.location.href = '/registrieren' }, 5000)
      }
    }
  } catch (error) {
    console.error('Error on Facebook login', error)
    showNotification({
      type: 'error',
      title: 'Facebook-Anmeldung fehlgeschlagen',
      message: 'Facebook-Anmeldung fehlgeschlagen'
    })
  } finally {
    processing.value = null
  }
}

clearForm()

onMounted(() => {
  // activate the form after the page is loaded
  if (isLoggedIn.value) {
    window.location.href = '/mein-profil'
  }
  preventSubmit.value = false

  // restore normal tab order by focusing the first field instead of the browser default at the top of the page
  if (document.activeElement === document.body) {
    emailInput.value?.focus()
  }
})
</script>

<template>
  <form class="bx-form bx-form--pur bx-typo--form"
        @submit.prevent="submit">
    <div :class="{ 'bx-has-error': errorMsg.email || showGlobalError,
                   'bx-has-global-error': showGlobalError }">
      <label for="login_email"
             class="bx-form__label">
        E-Mail-Adresse
      </label>
      <input id="login_email"
             ref="emailInput"
             v-model="user.email"
             name="email"
             type="text"
             placeholder="Deine E-Mail-Adresse"
             @focusout="clearValidationError('email')">
      <span v-if="errorMsg.email"
            class="bx-form__message bx-form__message--error">
        {{ errorMsg.email }}
      </span>
    </div>
    <div :class="{ 'bx-has-error': errorMsg.password || showGlobalError,
                   'bx-has-global-error': showGlobalError }">
      <label for="login_password"
             class="bx-form__label">
        Passwort
      </label>
      <a href="/passwort-vergessen"
         class="bx-typo--link bx-login__forgot-password">
        Passwort vergessen?
      </a>
      <PasswordField id="login_password"
                     :password="user.password"
                     placeholder="Dein Passwort"
                     autocomplete="new-password"
                     @input="emit('input', $event)"
                     @password-update="user.password = $event"
                     @focusout="clearValidationError('password')" />
      <span v-if="errorMsg.password"
            class="bx-form__message bx-form__message--error">
        {{ errorMsg.password }}
      </span>
    </div>

    <div v-if="showGlobalError"
         class="bx-form__message bx-form__message--error">
      Die Anmeldung ist fehlgeschlagen, weil die E-Mail-Adresse oder das Passwort ungültig sind. Bitte probiere es nochmal und achte auf Groß- und Kleinschreibung! Oder hast du dein
      <a href="/passwort-vergessen"
         class="bx-typo--link">
        <strong>Passwort vergessen?</strong>
      </a>
    </div>

    <div>
      <button type="submit"
              class="bx-form__button"
              :disabled="processing || preventSubmit"
              :data-loader="processing">
        Jetzt anmelden
      </button>
    </div>

    <FacebookLogin v-if="isClient && facebookEnabled"
                   label="Mit Facebook anmelden"
                   @facebook-login="onFacebookLogin" />

    <LoginMethodPreference v-if="isClient" />

    <div class="bx-login__register-now bx-typo--content-continuous-text">
      <p>
        <a href="/registrieren"
           class="bx-typo--link">
          <strong>Noch kein Konto? Jetzt registrieren!</strong>
        </a>
      </p>
    </div>
  </form>
</template>
